import { useEffect, useState } from 'react';
import { PLANNER_DAY_LONG, defaultPlannerCalendarContext } from '../../utils/mealPlanHelpers.js';
import { shouldAskPlanFreshness } from '../../utils/planFreshness.js';
import PlanFreshnessAsk from './PlanFreshnessAsk.jsx';
import { isNightOffMeal } from '../../utils/nightOff.js';
import PlannerWhySuffix from './PlannerWhySuffix.jsx';
import MealSlotStatusBadge from './MealSlotStatusBadge.jsx';
import MealRowActionsMenu from './MealRowActionsMenu.jsx';
import PastNightResolveRow from './PastNightResolveRow.jsx';
import './planner.css';

/*
 * Desktop meal row — single component, two states (assigned / unassigned).
 * Assigned meals use ⋮ menu for move/swap/remove (no drag handle).
 * Unassigned candidates remain draggable onto the calendar strip.
 */
export default function MealRow({
  meal,
  state,
  onClick,
  onUnassign,
  onMove,
  onSwap,
  onRemovePick,
  removePickBusy = false,
  isUserPick = false,
  isHighlighted = false,
  onDragStart,
  onDragEnd,
  full,
  dayDates = null,
  onViewRecipe,
  onMarkCooked,
  onMarkSkipped,
  onNightOff,
  isToday = false,
}) {
  const isAssigned = state === 'assigned';
  const isPast = isAssigned && !!meal.isPast;
  const dates = dayDates ?? defaultPlannerCalendarContext().dayDates;
  const cookable   = meal.have >= meal.need;
  const disabled   = !isAssigned && !!full;
  const canDrag = !isAssigned && !disabled;
  const showPastActions =
    isPast &&
    meal.status === 'planned' &&
    meal.recipeId &&
    Boolean(onMarkCooked || onMarkSkipped || onNightOff);
  const nightOff = isAssigned && isNightOffMeal(meal);
  const hideCookTime = isPast; // past rows stay compact: day + name + status (+ mark cooked/skip)

  const shopCount = meal.need - meal.have;
  const [freshnessSettled, setFreshnessSettled] = useState(false);
  useEffect(() => {
    setFreshnessSettled(false);
  }, [meal.planItemId, meal.recipeId, meal.agingItem?.id]);
  const showFreshness = !freshnessSettled && shouldAskPlanFreshness(meal);

  const nameRowMetaBits = [];
  if (!hideCookTime && meal.time && meal.time !== '—') nameRowMetaBits.push(meal.time);
  if (!isPast && typeof meal.serves === 'number' && meal.serves > 0) {
    nameRowMetaBits.push(`serves ${meal.serves}`);
  }
  const nameRowMeta =
    nameRowMetaBits.length > 0 ? ` · ${nameRowMetaBits.join(' · ')}` : null;

  function handleClick() {
    if (!isAssigned && !disabled && onClick) onClick();
  }

  function handleKeyDown(e) {
    if (isAssigned || disabled || !onClick) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onClick();
    }
  }

  const unassignedAriaLabel = !isAssigned
    ? (isUserPick ? `Your pick: ${meal.name}` : `Suggested: ${meal.name}`)
    : undefined;

  if (isAssigned && showPastActions) {
    return (
      <PastNightResolveRow
        meal={meal}
        dayDates={dates}
        isToday={isToday}
        onMarkCooked={onMarkCooked}
        onMarkSkipped={onMarkSkipped}
        onNightOff={onNightOff}
      />
    );
  }

  return (
    <div
      draggable={canDrag}
      onDragStart={canDrag ? onDragStart : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role={!isAssigned && !disabled ? 'button' : undefined}
      tabIndex={!isAssigned && !disabled ? 0 : undefined}
      aria-label={unassignedAriaLabel}
      data-plan-highlight={isHighlighted ? 'true' : undefined}
      className={[
        'meal-row',
        isAssigned ? 'assigned' : 'unassigned',
        !isAssigned && isUserPick ? 'meal-row--user-pick' : '',
        isHighlighted ? 'meal-row--plan-highlight' : '',
        isPast ? 'past' : '',
        nightOff ? 'night-off' : '',
        isAssigned && isToday ? 'meal-row--today' : '',
        disabled    ? 'disabled' : '',
      ].filter(Boolean).join(' ')}
    >
      {!isAssigned && (
        <div
          className="meal-row-drag-handle"
          title="Drag onto a day to assign"
          aria-hidden="true"
        >
          ⋮⋮
        </div>
      )}

      {isAssigned && meal.assignedTo && (
        <div className="meal-row-day-chip">
          <div
            className="meal-row-day-chip-bar"
            style={nightOff ? undefined : { background: meal.swatch }}
          />
          <div className="meal-row-day-label">{PLANNER_DAY_LONG[meal.assignedTo]}</div>
          <div className="meal-row-day-date">{dates[meal.assignedTo]}</div>
          {isToday && <div className="meal-row-today-dot" aria-hidden="true" />}
        </div>
      )}

      {!isAssigned && !isUserPick && (
        <div
          className="meal-row-swatch"
          style={{ background: meal.swatch }}
          aria-hidden="true"
        />
      )}

      <div className="meal-row-info">
        {nightOff ? (
          <div className="meal-row-name-row">
            <MealSlotStatusBadge meal={meal} />
          </div>
        ) : (
          <>
            <div className="meal-row-name-row">
              <span className="planner-meal-name">{meal.name}</span>
              {nameRowMeta && (
                <span className="meal-row-meta">{nameRowMeta}</span>
              )}
              {meal.repeat && !isPast && (
                <span className="meal-row-repeat-badge">
                  {isAssigned ? 'Repeat' : 'Cooked recently'}
                </span>
              )}
              {isAssigned && <MealSlotStatusBadge meal={meal} />}
            </div>

            {isAssigned && isPast ? null : isAssigned ? (
              <div className="meal-row-sub">
                {showFreshness ? null : cookable ? (
                  <span className="chip green chip-xs">Cookable now</span>
                ) : (
                  <span className="meal-row-slot-status shop">
                    {shopCount} to shop
                  </span>
                )}
                <span className="meal-row-meta">
                  {(showFreshness && cookable) ? '' : '· '}
                  Pantry match {meal.have}/{meal.need}
                </span>
                <PlannerWhySuffix meal={meal} />
              </div>
            ) : (
              <div className="meal-row-sub">
                <span className={`meal-row-source ${isUserPick ? 'match' : meal.source === 'match' ? 'match' : 'added'}`}>
                  {isUserPick ? 'Your pick' : meal.source === 'match' ? 'Pantry match' : 'From Recipes'}
                </span>
                <span className="meal-row-meta">
                  · {meal.have}/{meal.need} in pantry
                </span>
                <PlannerWhySuffix meal={meal} />
              </div>
            )}

            {showFreshness && (
              <PlanFreshnessAsk meal={meal} onSettled={() => setFreshnessSettled(true)} />
            )}

            {isAssigned && onViewRecipe && !isPast && (
              <button
                type="button"
                className="planner-pick-remove-link planner-recipe-details-link meal-row-details-under"
                onClick={e => { e.stopPropagation(); onViewRecipe(); }}
              >
                Recipe Details →
              </button>
            )}
          </>
        )}
      </div>

      {(!isAssigned || !isPast) && (
      <div className="meal-row-actions">
        {isAssigned ? (
          <div className="meal-row-unassigned-actions">
            {!isPast && (
              <MealRowActionsMenu
                nightOff={nightOff}
                onMove={onMove}
                onSwap={onSwap}
                onRemove={onUnassign}
              />
            )}
          </div>
        ) : (
          <div className="meal-row-unassigned-actions">
            <span
              className={`meal-row-add-cta${disabled ? ' planner-add-cta-disabled' : ' planner-add-cta-open'}`}
            >
              {disabled ? 'Week full' : 'Add to next open →'}
            </span>
            {onRemovePick && (
              <button
                type="button"
                className="planner-pick-remove-link"
                disabled={removePickBusy}
                onClick={e => { e.stopPropagation(); onRemovePick(); }}
              >
                Remove
              </button>
            )}
          </div>
        )}
      </div>
      )}
    </div>
  );
}
